/**
 * @module Kalender
 * @description Kalender-Seite mit Wochen-/Tagesansicht, Outlook-Events,
 * Zeitbloecken (Hintergrund-Layer) und Drag&Drop fuer ungeplante Tasks.
 * 3 Layer:
 * 1. Zeitbloecke (Hintergrund, farbig, Template aus calendar_time_blocks)
 * 2. Outlook-Termine (Vordergrund, aus /me/calendarView)
 * 3. Tasks (geplante user_tasks aus /api/user-tasks/week)
 * Right-Sidebar: Ungeplante Tasks zum reinziehen.
 *
 * @route /kalender
 * @since v2.316.0
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
 Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus,
 Settings as SettingsIcon, Link2, Check, X, AlertTriangle, Trash2, Clock,
 Sparkles,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

// ─── Helpers ────────────────────────────────────────────────────────────────
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 – 19:00
const HOUR_PX = 56; // px pro Stunde
const DAY_LABELS = ['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa'];

function startOfWeek(d) {
 const x = new Date(d);
 x.setHours(0, 0, 0, 0);
 const dow = x.getDay();
 const diff = (dow + 6) % 7; // Mo=0
 x.setDate(x.getDate() - diff);
 return x;
}

function addDays(d, n) {
 const x = new Date(d);
 x.setDate(x.getDate() + n);
 return x;
}

function isoDate(d) {
 const x = new Date(d);
 x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
 return x.toISOString().slice(0, 10);
}

function fmtTime(d) {
 return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function parseTimeStr(s) {
 if (!s) return [0, 0];
 const [h, m] = String(s).split(':').map(Number);
 return [h || 0, m || 0];
}

function timeToY(hours, minutes) {
 const start = HOURS[0];
 return ((hours - start) + minutes / 60) * HOUR_PX;
}

function dateToY(date) {
 const d = new Date(date);
 return timeToY(d.getHours(), d.getMinutes());
}

function durationToHeight(startDate, endDate) {
 const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
 return Math.max(20, (ms / 3600000) * HOUR_PX);
}

const BLOCK_TYPES = (t) => ({
 deep_work: { label: t('kalender.types.deepWork', 'Deep Work'), color: '#338edc' },
 sales: { label: t('kalender.types.sales', 'Sales'), color: '#22D3EE' },
 team: { label: t('kalender.types.team', 'Team'), color: '#6366F1' },
 admin: { label: t('kalender.types.admin', 'Admin'), color: '#9CA3AF' },
 pause: { label: t('kalender.types.pause', 'Pause'), color: '#10B981' },
 meeting: { label: t('kalender.types.meeting', 'Meeting'), color: '#F59E0B' },
});

// ─── Block Editor Modal ─────────────────────────────────────────────────────
function BlockModal({ open, block, defaultDay, defaultHour, onClose, onSaved, t }) {
 const [form, setForm] = useState({
 day_of_week: 1,
 start_time: '09:00',
 end_time: '11:00',
 block_type: 'deep_work',
 label: '',
 color: '',
 is_bookable: false,
 outlook_status: 'busy',
 });
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (!open) return;
 setError(null);
 if (block) {
 setForm({
 day_of_week: block.day_of_week,
 start_time: String(block.start_time).slice(0, 5),
 end_time: String(block.end_time).slice(0, 5),
 block_type: block.block_type,
 label: block.label || '',
 color: block.color || '',
 is_bookable: !!block.is_bookable,
 outlook_status: block.outlook_status || 'busy',
 });
 } else {
 setForm({
 day_of_week: defaultDay ?? 1,
 start_time: defaultHour ? `${String(defaultHour).padStart(2, '0')}:00` : '09:00',
 end_time: defaultHour ? `${String(defaultHour + 2).padStart(2, '0')}:00` : '11:00',
 block_type: 'deep_work',
 label: '',
 color: '',
 is_bookable: false,
 outlook_status: 'busy',
 });
 }
 }, [open, block, defaultDay, defaultHour]);

 if (!open) return null;

 const submit = async (e) => {
 e.preventDefault();
 setSaving(true);
 setError(null);
 try {
 const url = block ? `/api/calendar/blocks/${block.id}` : '/api/calendar/blocks';
 const method = block ? 'PUT' : 'POST';
 const r = await f(url, {
 method,
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(form),
 });
 if (!r.ok) {
 const j = await r.json().catch(() => ({}));
 throw new Error(j.error || `${r.status}`);
 }
 onSaved();
 onClose();
 } catch (err) {
 setError(err.message);
 } finally {
 setSaving(false);
 }
 };

 const remove = async () => {
 if (!block) return;
 if (!confirm(t('kalender.confirmDelete', 'Wirklich loeschen?'))) return;
 setSaving(true);
 try {
 await f(`/api/calendar/blocks/${block.id}`, { method: 'DELETE' });
 onSaved();
 onClose();
 } catch (err) {
 setError(err.message);
 } finally {
 setSaving(false);
 }
 };

 const types = BLOCK_TYPES(t);
 const dayNames = [
 t('kalender.day.so', 'So'), t('kalender.day.mo', 'Mo'), t('kalender.day.di', 'Di'),
 t('kalender.day.mi', 'Mi'), t('kalender.day.do', 'Do'), t('kalender.day.fr', 'Fr'),
 t('kalender.day.sa', 'Sa'),
 ];

 return (
 <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
 <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg-card,#fff)] rounded-xl border w-full max-w-md max-h-[90vh] overflow-y-auto" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <h2 className="text-base font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {block ? t('kalender.editBlock', 'Zeitblock bearbeiten') : t('kalender.newBlock', 'Neuer Zeitblock')}
 </h2>
 <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]">
 <X className="w-5 h-5" />
 </button>
 </div>
 <form onSubmit={submit} className="p-5 space-y-3">
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.weekday', 'Wochentag')}
 </label>
 <select
 value={form.day_of_week}
 onChange={(e) => setForm(s => ({ ...s, day_of_week: parseInt(e.target.value, 10) }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 >
 {[1, 2, 3, 4, 5, 6, 0].map((d) => (
 <option key={d} value={d}>{dayNames[d]}</option>
 ))}
 </select>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.startTime', 'Start')}
 </label>
 <input
 type="time"
 value={form.start_time}
 onChange={(e) => setForm(s => ({ ...s, start_time: e.target.value }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.endTime', 'Ende')}
 </label>
 <input
 type="time"
 value={form.end_time}
 onChange={(e) => setForm(s => ({ ...s, end_time: e.target.value }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 </div>
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.blockType', 'Typ')}
 </label>
 <select
 value={form.block_type}
 onChange={(e) => setForm(s => ({ ...s, block_type: e.target.value }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 >
 {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.label', 'Bezeichnung (optional)')}
 </label>
 <input
 type="text"
 value={form.label}
 onChange={(e) => setForm(s => ({ ...s, label: e.target.value }))}
 placeholder={types[form.block_type]?.label}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 <input
 type="checkbox"
 checked={form.is_bookable}
 onChange={(e) => setForm(s => ({ ...s, is_bookable: e.target.checked }))}
 />
 {t('kalender.isBookable', 'Buchbar fuer Team')}
 </label>
 <span />
 </div>

 {error && (
 <div className="text-xs text-red-500 flex items-center gap-1.5">
 <AlertTriangle className="w-4 h-4" /> {error}
 </div>
 )}

 <div className="flex items-center justify-between pt-2">
 {block ? (
 <button
 type="button"
 onClick={remove}
 disabled={saving}
 className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1"
 >
 <Trash2 className="w-4 h-4" /> {t('kalender.delete', 'Loeschen')}
 </button>
 ) : <span />}
 <div className="flex items-center gap-2">
 <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.cancel', 'Abbrechen')}
 </button>
 <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2876b8] disabled:opacity-50">
 {saving ? t('kalender.saving', 'Speichern...') : t('kalender.save', 'Speichern')}
 </button>
 </div>
 </div>
 </form>
 </div>
 </div>
 );
}

// ─── Event-Erstellungs-Modal ─────────────────────────────────────────────────
function EventModal({ open, onClose, onSave, t }) {
 const [form, setForm] = useState({ subject: '', start: '', end: '', description: '' });
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (!open) return;
 setError(null);
 // Default: heute, naechste volle Stunde
 const now = new Date();
 now.setMinutes(0, 0, 0);
 now.setHours(now.getHours() + 1);
 const end = new Date(now);
 end.setHours(end.getHours() + 1);
 const fmt = (d) => {
 const y = d.getFullYear();
 const m = String(d.getMonth() + 1).padStart(2, '0');
 const day = String(d.getDate()).padStart(2, '0');
 const h = String(d.getHours()).padStart(2, '0');
 const min = String(d.getMinutes()).padStart(2, '0');
 return `${y}-${m}-${day}T${h}:${min}`;
 };
 setForm({ subject: '', start: fmt(now), end: fmt(end), description: '' });
 }, [open]);

 if (!open) return null;

 const submit = async (e) => {
 e.preventDefault();
 if (!form.subject || !form.start || !form.end) return;
 setSaving(true);
 setError(null);
 try {
 await onSave({
 subject: form.subject,
 start: new Date(form.start).toISOString(),
 end: new Date(form.end).toISOString(),
 body: form.description || undefined,
 });
 onClose();
 } catch (err) {
 setError(err.message);
 } finally {
 setSaving(false);
 }
 };

 return (
 <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
 <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg-card,#fff)] rounded-xl border w-full max-w-md" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <h2 className="text-base font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {t('kalender.newEvent', 'Neuer Termin')}
 </h2>
 <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]">
 <X className="w-5 h-5" />
 </button>
 </div>
 <form onSubmit={submit} className="p-5 space-y-3">
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.eventSubject', 'Betreff')}
 </label>
 <input
 type="text"
 value={form.subject}
 onChange={(e) => setForm(s => ({ ...s, subject: e.target.value }))}
 placeholder={t('kalender.eventSubjectPh', 'z.B. Meeting mit Team')}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 autoFocus
 />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.startTime', 'Start')}
 </label>
 <input
 type="datetime-local"
 value={form.start}
 onChange={(e) => setForm(s => ({ ...s, start: e.target.value }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.endTime', 'Ende')}
 </label>
 <input
 type="datetime-local"
 value={form.end}
 onChange={(e) => setForm(s => ({ ...s, end: e.target.value }))}
 className="w-full px-3 py-2 rounded-lg border text-sm"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 </div>
 <div>
 <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.eventDescription', 'Beschreibung (optional)')}
 </label>
 <textarea
 value={form.description}
 onChange={(e) => setForm(s => ({ ...s, description: e.target.value }))}
 rows={2}
 className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
 style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 </div>
 {error && (
 <div className="text-xs text-red-500 flex items-center gap-1.5">
 <AlertTriangle className="w-4 h-4" /> {error}
 </div>
 )}
 <div className="flex items-center justify-end gap-2 pt-2">
 <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.cancel', 'Abbrechen')}
 </button>
 <button type="submit" disabled={saving || !form.subject} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2876b8] disabled:opacity-50">
 {saving ? t('kalender.saving', 'Speichern...') : t('kalender.createEvent', 'Termin erstellen')}
 </button>
 </div>
 </form>
 </div>
 </div>
 );
}

// ─── Hauptseite ─────────────────────────────────────────────────────────────
export default function Kalender() {
 const { t } = useLanguage();
 const navigate = useNavigate();
 const location = useLocation();

 const [view, setView] = useState('week'); // 'week' | 'day'
 const [refDate, setRefDate] = useState(() => new Date());
 const [status, setStatus] = useState({ connected: false });
 const [events, setEvents] = useState([]);
 const [blocks, setBlocks] = useState([]);
 const [tasks, setTasks] = useState([]);
 const [unplanned, setUnplanned] = useState([]);
 const [loading, setLoading] = useState(false);
 const [eventModalOpen, setEventModalOpen] = useState(false);
 const [banner, setBanner] = useState(null);
 const [editBlock, setEditBlock] = useState(null);
 const [blockModalOpen, setBlockModalOpen] = useState(false);
 const [blockDefault, setBlockDefault] = useState({ day: null, hour: null });
 const [draggedTask, setDraggedTask] = useState(null);

 const types = useMemo(() => BLOCK_TYPES(t), [t]);

 const range = useMemo(() => {
 if (view === 'day') {
 const d = new Date(refDate);
 d.setHours(0, 0, 0, 0);
 return { start: d, end: addDays(d, 1), days: [d] };
 }
 const start = startOfWeek(refDate);
 const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
 return { start, end: addDays(start, 7), days };
 }, [view, refDate]);

 // ─── Initial: O365 Status + URL-Banner ───
 useEffect(() => {
 f('/api/calendar/status').then(r => r.ok ? r.json() : { connected: false }).then(setStatus).catch(() => {});
 const params = new URLSearchParams(location.search);
 const o365 = params.get('o365');
 if (o365 === 'connected') {
 setBanner({ type: 'success', text: t('kalender.connectedOk', 'O365 erfolgreich verbunden') });
 } else if (o365 === 'error') {
 setBanner({ type: 'error', text: params.get('msg') || t('kalender.connectError', 'Verbindung fehlgeschlagen') });
 }
 }, [location.search, t]);

 // ─── Daten laden ───
 const loadAll = useCallback(async () => {
 setLoading(true);
 try {
 // Tasks (geplant + ungeplant)
 const wkRes = await f('/api/user-tasks/week').then(r => r.ok ? r.json() : { tasks: [] });
 const wkTasks = wkRes.tasks || [];
 const planned = wkTasks.filter(t => t.planned_date);
 const unp = wkTasks.filter(t => !t.planned_date);
 setTasks(planned);
 setUnplanned(unp);

 // Bloecke
 const bRes = await f('/api/calendar/blocks').then(r => r.ok ? r.json() : { blocks: [] });
 setBlocks(bRes.blocks || []);

 // Outlook-Events (nur wenn verbunden)
 if (status.connected) {
 const startIso = range.start.toISOString();
 const endIso = range.end.toISOString();
 const evRes = await f(`/api/calendar/events?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`);
 if (evRes.ok) {
 const j = await evRes.json();
 setEvents(j.events || []);
 } else if (evRes.status === 401) {
 setStatus({ connected: false });
 }
 } else {
 setEvents([]);
 }
 } finally {
 setLoading(false);
 }
 }, [range, status.connected]);

 useEffect(() => { loadAll(); }, [loadAll]);

 // Auto-Refresh alle 60s wenn verbunden
 useEffect(() => {
 if (!status.connected) return;
 const id = setInterval(loadAll, 60000);
 return () => clearInterval(id);
 }, [status.connected, loadAll]);

 // ─── Aktionen ───
 const connectO365 = () => { window.location.href = `${API}/api/calendar/login`; };
 const disconnectO365 = async () => {
 if (!confirm(t('kalender.confirmDisconnect', 'O365 wirklich trennen?'))) return;
 await f('/api/calendar/disconnect', { method: 'POST' });
 setStatus({ connected: false });
 setEvents([]);
 };
 const createOutlookEvent = async (eventData) => {
 try {
 const r = await f('/api/calendar/events', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(eventData),
 });
 if (r.ok) {
 setBanner({ type: 'success', text: t('kalender.eventCreated', 'Termin erstellt') });
 loadAll();
 } else {
 const j = await r.json().catch(() => ({}));
 setBanner({ type: 'error', text: j.error || 'Fehler beim Erstellen' });
 }
 } catch (err) {
 setBanner({ type: 'error', text: err.message });
 }
 };

 const planTask = async (taskId, dateIso, startTime) => {
 try {
 const body = {
 planned_date: dateIso,
 planned_start: startTime || null,
 planned_end: null,
 };
 await f(`/api/user-tasks/${taskId}/plan`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(body),
 });
 loadAll();
 } catch (err) {
 console.error(err);
 }
 };

 const handleDropOnSlot = (date, hour) => (e) => {
 e.preventDefault();
 if (!draggedTask) return;
 const startTime = `${String(hour).padStart(2, '0')}:00`;
 planTask(draggedTask.id, isoDate(date), startTime);
 setDraggedTask(null);
 };

 const openNewBlock = (day, hour) => {
 setEditBlock(null);
 setBlockDefault({ day, hour });
 setBlockModalOpen(true);
 };

 const openEditBlock = (block) => {
 setEditBlock(block);
 setBlockDefault({ day: null, hour: null });
 setBlockModalOpen(true);
 };

 // ─── Rendering Helpers ───
 const dayLabel = (date) => {
 const dow = date.getDay();
 return t(`kalender.day.${DAY_LABELS[dow]}`, ['So','Mo','Di','Mi','Do','Fr','Sa'][dow]);
 };

 const todayStr = isoDate(new Date());
 const isToday = (d) => isoDate(d) === todayStr;

 const blocksForDay = (date) => {
 const dow = date.getDay();
 return blocks.filter(b => b.day_of_week === dow);
 };

 const eventsForDay = (date) => {
 const day = isoDate(date);
 return events.filter(ev => ev.start && isoDate(ev.start) === day);
 };

 const tasksForDay = (date) => {
 const day = isoDate(date);
 return tasks.filter(t => t.planned_date && String(t.planned_date).slice(0, 10) === day);
 };

 // ─── Render ───────────────────────────────────────────────────────────────
 return (
 <div className="flex-1 flex flex-col overflow-hidden">
 {/* Header */}
 <div className="px-6 pt-5 pb-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center gap-3">
 <div className="w-9 h-9 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
 <CalendarIcon className="w-5 h-5 text-[#338edc]" />
 </div>
 <div>
 <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {t('kalender.title', 'Kalender')}
 </h1>
 <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('kalender.subtitle', 'Termine, Zeitbloecke und Aufgaben')}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {status.connected ? (
 <div className="flex items-center gap-2">
 <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
 <Check className="w-3 h-3" /> {status.account_email}
 </span>
 <button onClick={disconnectO365} className="px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-[var(--bg-inset,#F8F9FC)]" style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.disconnect', 'Trennen')}
 </button>
 </div>
 ) : (
 <button onClick={connectO365} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2876b8]">
 <Link2 className="w-4 h-4" /> {t('kalender.connectO365', 'O365 verbinden')}
 </button>
 )}
 </div>
 </div>

 {/* Banner */}
 {banner && (
 <div className={`mx-6 mt-3 px-4 py-2 rounded-lg text-sm flex items-center justify-between ${banner.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
 <span className="flex items-center gap-2">
 {banner.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
 {banner.text}
 </span>
 <button onClick={() => setBanner(null)}><X className="w-4 h-4" /></button>
 </div>
 )}

 {/* Toolbar */}
 <div className="px-6 py-3 flex items-center justify-between border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setRefDate(view === 'day' ? addDays(refDate, -1) : addDays(refDate, -7))}
 className="p-1.5 rounded-lg border hover:bg-[var(--bg-inset,#F8F9FC)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 >
 <ChevronLeft className="w-4 h-4" />
 </button>
 <button
 onClick={() => setRefDate(new Date())}
 className="px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-[var(--bg-inset,#F8F9FC)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 >
 {t('kalender.today', 'Heute')}
 </button>
 <button
 onClick={() => setRefDate(view === 'day' ? addDays(refDate, 1) : addDays(refDate, 7))}
 className="p-1.5 rounded-lg border hover:bg-[var(--bg-inset,#F8F9FC)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 >
 <ChevronRight className="w-4 h-4" />
 </button>
 <span className="ml-3 text-sm font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {view === 'day'
 ? refDate.toLocaleDateString('de-AT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
 : `${range.days[0].toLocaleDateString('de-AT', { day: '2-digit', month: 'short' })} – ${range.days[6].toLocaleDateString('de-AT', { day: '2-digit', month: 'short', year: 'numeric' })}`
 }
 </span>
 </div>
 <div className="flex items-center gap-2">
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border flex" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 {['week', 'day'].map((m) => (
 <button
 key={m}
 onClick={() => setView(m)}
 className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${view === m ? 'bg-white shadow-sm' : ''}`}
 style={{ color: view === m ? 'var(--text-primary, #1A1A2E)' : 'var(--text-tertiary, #6B7280)' }}
 >
 {m === 'week' ? t('kalender.week', 'Woche') : t('kalender.day', 'Tag')}
 </button>
 ))}
 </div>
 <button
 onClick={() => openNewBlock(refDate.getDay(), 9)}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-[var(--bg-inset,#F8F9FC)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 >
 <SettingsIcon className="w-3.5 h-3.5" /> {t('kalender.editBlocks', 'Zeitbloecke')}
 </button>
 <button
 onClick={() => navigate('/kalender/buchen')}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-[var(--bg-inset,#F8F9FC)]"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 title={t('kalender.bookTitle', 'Termin bei einem Kollegen buchen')}
 >
 <CalendarIcon className="w-3.5 h-3.5" /> {t('kalender.bookSlot', 'Termin buchen')}
 </button>
 {status.connected && (
 <button
 onClick={() => setEventModalOpen(true)}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#338edc] hover:bg-[#2876b8]"
 >
 <Plus className="w-3.5 h-3.5" /> {t('kalender.newEvent', 'Neuer Termin')}
 </button>
 )}
 </div>
 </div>

 {/* Main Layout: Calendar + Sidebar */}
 <div className="flex-1 flex overflow-hidden">
 {/* Calendar Grid */}
 <div className="flex-1 overflow-auto">
 <div className="min-w-[700px]">
 {/* Day headers */}
 <div className="flex sticky top-0 bg-[var(--bg-card,#fff)] z-10 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="w-14 flex-shrink-0" />
 {range.days.map((d, i) => (
 <div key={i} className="flex-1 min-w-0 text-center py-2 border-l" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {dayLabel(d)}
 </div>
 <div className={`text-base font-bold ${isToday(d) ? 'text-[#338edc]' : ''}`} style={{ color: isToday(d) ? undefined : 'var(--text-primary, #1A1A2E)' }}>
 {d.getDate()}
 </div>
 </div>
 ))}
 </div>

 {/* Time grid */}
 <div className="flex relative" style={{ height: HOURS.length * HOUR_PX }}>
 {/* Hour labels */}
 <div className="w-14 flex-shrink-0 relative">
 {HOURS.map((h, i) => (
 <div key={h} className="absolute right-2 text-[10px]" style={{ top: i * HOUR_PX - 6, color: 'var(--text-tertiary, #6B7280)' }}>
 {String(h).padStart(2, '0')}:00
 </div>
 ))}
 </div>

 {/* Day columns */}
 {range.days.map((d, idx) => (
 <DayColumn
 key={idx}
 date={d}
 blocks={blocksForDay(d)}
 events={eventsForDay(d)}
 tasks={tasksForDay(d)}
 types={types}
 isToday={isToday(d)}
 onSlotClick={(hour) => openNewBlock(d.getDay(), hour)}
 onBlockClick={openEditBlock}
 onDrop={handleDropOnSlot(d, 9)}
 onSlotDrop={(hour) => (e) => {
 e.preventDefault();
 if (!draggedTask) return;
 planTask(draggedTask.id, isoDate(d), `${String(hour).padStart(2, '0')}:00`);
 setDraggedTask(null);
 }}
 />
 ))}
 </div>
 </div>
 </div>

 {/* Sidebar: Ungeplante Tasks */}
 <div className="w-72 flex-shrink-0 border-l overflow-y-auto bg-[var(--bg-inset,#F8F9FC)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="px-4 py-3 border-b sticky top-0 bg-[var(--bg-inset,#F8F9FC)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <div className="flex items-center justify-between">
 <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('kalender.unplanned', 'Ungeplant')}
 </h3>
 <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--bg-card,#fff)] border" style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-tertiary, #6B7280)' }}>
 {unplanned.length}
 </span>
 </div>
 <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('kalender.dragHint', 'Per Drag&Drop in den Kalender ziehen')}
 </p>
 </div>
 <div className="p-3 space-y-2">
 {unplanned.length === 0 ? (
 <div className="text-center py-8 text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 <Sparkles className="w-6 h-6 mx-auto mb-2 opacity-40" />
 {t('kalender.allPlanned', 'Alle Aufgaben geplant')}
 </div>
 ) : (
 unplanned.map((task) => (
 <div
 key={task.id}
 draggable
 onDragStart={() => setDraggedTask(task)}
 onDragEnd={() => setDraggedTask(null)}
 className="bg-[var(--bg-card,#fff)] border rounded-lg px-3 py-2 cursor-grab hover:border-[#338edc] transition-colors"
 style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
 >
 <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {task.title}
 </p>
 {task.estimated_minutes && (
 <span className="inline-flex items-center gap-1 mt-1 text-[10px]" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 <Clock className="w-3 h-3" /> {task.estimated_minutes}min
 </span>
 )}
 {task.priority === 'urgent' && (
 <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-red-500">
 {t('kalender.urgent', 'Dringend')}
 </span>
 )}
 </div>
 ))
 )}
 </div>
 <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
 <button
 onClick={() => navigate('/meine-aufgaben')}
 className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-white"
 style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
 >
 {t('kalender.openTasks', 'Zu Meine Aufgaben')}
 </button>
 </div>
 </div>
 </div>

 {/* Modals */}
 <BlockModal
 open={blockModalOpen}
 block={editBlock}
 defaultDay={blockDefault.day}
 defaultHour={blockDefault.hour}
 onClose={() => setBlockModalOpen(false)}
 onSaved={loadAll}
 t={t}
 />
 <EventModal
 open={eventModalOpen}
 onClose={() => setEventModalOpen(false)}
 onSave={createOutlookEvent}
 t={t}
 />
 </div>
 );
}

// ─── Day Column ─────────────────────────────────────────────────────────────
function DayColumn({ date, blocks, events, tasks, types, isToday, onSlotClick, onBlockClick, onSlotDrop }) {
 return (
 <div className="flex-1 min-w-0 relative border-l" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: isToday ? 'rgba(51, 142, 220, 0.03)' : undefined }}>
 {/* Hour grid lines + drop targets */}
 {HOURS.map((h, i) => (
 <div
 key={h}
 className="absolute left-0 right-0 border-t hover:bg-[var(--bg-inset,#F8F9FC)]/40 cursor-pointer"
 style={{ top: i * HOUR_PX, height: HOUR_PX, borderColor: 'var(--border-card, #E5E7EB)' }}
 onClick={() => onSlotClick(h)}
 onDragOver={(e) => e.preventDefault()}
 onDrop={onSlotDrop(h)}
 />
 ))}

 {/* Layer 1: Time blocks (background) */}
 {blocks.map((b) => {
 const [sh, sm] = parseTimeStr(b.start_time);
 const [eh, em] = parseTimeStr(b.end_time);
 const top = timeToY(sh, sm);
 const height = Math.max(20, ((eh + em / 60) - (sh + sm / 60)) * HOUR_PX);
 const color = b.color || types[b.block_type]?.color || '#338edc';
 return (
 <div
 key={`b-${b.id}`}
 className="absolute left-1 right-1 rounded-lg cursor-pointer transition-opacity hover:opacity-90"
 style={{
 top, height,
 background: `${color}1A`, // 10% opacity
 borderLeft: `3px solid ${color}`,
 zIndex: 1,
 }}
 onClick={(e) => { e.stopPropagation(); onBlockClick(b); }}
 >
 <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color }}>
 {b.label || types[b.block_type]?.label || b.block_type}
 </div>
 </div>
 );
 })}

 {/* Layer 2: Outlook Events */}
 {events.map((ev) => {
 if (!ev.start || !ev.end) return null;
 const top = dateToY(ev.start);
 const height = durationToHeight(ev.start, ev.end);
 return (
 <div
 key={`ev-${ev.id}`}
 className="absolute left-3 right-2 rounded-lg bg-white border-l-4 border-[#338edc] shadow-sm px-2 py-1 cursor-pointer hover:shadow-sm transition-shadow"
 style={{ top, height, zIndex: 3, borderColor: '#338edc' }}
 title={ev.subject}
 onClick={(e) => { e.stopPropagation(); ev.web_link && window.open(ev.web_link, '_blank'); }}
 >
 <div className="text-[11px] font-semibold leading-tight truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
 {ev.subject}
 </div>
 <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {fmtTime(new Date(ev.start))} – {fmtTime(new Date(ev.end))}
 </div>
 </div>
 );
 })}

 {/* Layer 3: Geplante Tasks */}
 {tasks.map((task) => {
 if (!task.planned_start) return null;
 const [h, m] = parseTimeStr(task.planned_start);
 const top = timeToY(h, m);
 const height = task.estimated_minutes ? (task.estimated_minutes / 60) * HOUR_PX : 30;
 return (
 <div
 key={`t-${task.id}`}
 className="absolute left-2 right-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 px-2 py-1 cursor-pointer"
 style={{ top, height: Math.max(24, height), zIndex: 4 }}
 title={task.title}
 >
 <div className="text-[11px] font-semibold leading-tight truncate text-amber-900">
 {task.title}
 </div>
 </div>
 );
 })}
 </div>
 );
}
